import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AppSidebar } from './app-sidebar/app-sidebar';
import { AppTopbarUserMenu } from '../topbar/app-topbar-user-menu';
import { AppSwitcher } from '../topbar/app-switcher';
import { SalesCopilotModal } from './app-sidebar/sales-copilot-modal';
import { useSidebarStore } from './app-sidebar/sidebar-store';
import { useSalesCopilotModalStore } from '../../stores/sales-copilot-modal-store';
import {
  SIDEBAR_DIMENSIONS,
  ANIMATION_DURATION,
  Z_INDEX,
} from './app-sidebar/constants';
import { NavigationConfig } from './app-sidebar/types';
import {
  OrgContextBanner,
  ORG_CONTEXT_BANNER_HEIGHT_PX,
} from './org-context-banner';
import type { RbacClaim } from '../../../models/Rbac';
import { isV2User } from '../../utils/rbac';
import {
  fetchAiInsightsEnabled,
  fetchTreeInsightsEnabled,
} from '../model/rgipChatApi';
import { LAYOUT, resolveLayoutFit, topbarFor } from './layoutFit';
import { useSidePanelStore } from './side-panel/side-panel-store';
import { ResponsiveLayoutContext } from './responsive-layout-context';
import { PanelHostContext } from './side-panel/panel-host-context';
import { ShellPanel } from './side-panel/ShellPanel';
import { TopbarSlotContext } from './topbar-slot';
import './shell.css';

interface User {
  email: string;
}

/**
 * Everything `document360/widget.js` mounts on `document.body`, so turning the
 * widget off can take its UI away and not just its script tag.
 *
 * Read off the widget source rather than guessed: it creates the iframe and the
 * legacy container by id, and the welcome popover and its stylesheet by the
 * constants of the same name. An id that stops existing simply matches nothing,
 * so a stale entry here is inert.
 */
const D360_NODE_IDS = [
  'document360-widget',
  'document360-widget-iframe',
  'document360-widget-legacy',
  'document360-welcome-popover',
  'document360-welcome-popover-styles',
];

interface RootLayoutProps {
  children: ReactNode;
  navConfig: NavigationConfig;
  logo: ReactNode;
  collapsedLogo?: ReactNode;
  logoHref?: string;
  user?: User;
  tenantId?: string;
  isSuperKudu?: boolean;
  primaryTenantId?: string;
  /** v2 RBAC claims from the store. v1 users (null) keep the legacy topbar. */
  rbac?: RbacClaim | null;
}

/**
 * The two shell flags, published together.
 *
 * A component rather than two nested providers written out in `RootLayout`, so
 * that adding the second one does not reindent the whole shell and bury a
 * fourteen-line change in a hundred and forty lines of whitespace.
 */
function ShellFlags({
  shellLayout,
  panelHosting,
  topbarSlot,
  children,
}: {
  shellLayout: boolean;
  panelHosting: boolean;
  topbarSlot: HTMLElement | null;
  children: ReactNode;
}) {
  return (
    <ResponsiveLayoutContext.Provider value={shellLayout}>
      <PanelHostContext.Provider value={panelHosting}>
        <TopbarSlotContext.Provider value={topbarSlot}>
          {children}
        </TopbarSlotContext.Provider>
      </PanelHostContext.Provider>
    </ResponsiveLayoutContext.Provider>
  );
}

export function RootLayout({
  children,
  navConfig,
  logo,
  collapsedLogo,
  logoHref,
  user,
  tenantId,
  isSuperKudu,
  primaryTenantId,
  rbac,
}: RootLayoutProps) {
  const showAppSwitcher = isV2User(rbac ?? null);
  const { isCollapsed, setForcedCollapsed, suppressTransition } =
    useSidebarStore();
  const sidePanel = useSidePanelStore();

  // The shell does two jobs, and they are gated separately because they ship to
  // different tenants.
  //
  //   `shellLayout`  the pages are laid out by the new shell: the content
  //                  container query, the measured gutter and top bar, the nav
  //                  fold when the window alone is too narrow. The revamp's own
  //                  master switch, not a flag of its own: the shell exists to
  //                  make those surfaces usable, so a tenant on the revamp
  //                  should not need a second toggle to get a layout that fits.
  //
  //   `panelHosting` there is a column for the chat to dock into, which is what
  //                  carries the resize handle and the close control. Follows
  //                  the chat's own flag, so the assistant can be given to a
  //                  tenant that is not on the revamp.
  //
  // One value for both until 2026-09-10, and it was the tree flag, so an AI-only
  // tenant got the chat as a fixed floating card with nothing to size or shut it
  // by. Merging them back means picking which of those two tenants to break.
  //
  // `null` while they resolve, and treated exactly as `false`. The insights flags
  // gate additions, so drawing nothing during their fetch is right; these gate
  // the frame every page lives in, and a beat of no frame is a blank screen. The
  // cost is a flash of today's shell before the new one, which is strictly
  // better than blocking first paint.
  const [shellLayoutFlag, setShellLayoutFlag] = useState<boolean | null>(null);
  const [panelHostingFlag, setPanelHostingFlag] = useState<boolean | null>(
    null
  );
  const shellLayout = shellLayoutFlag === true;
  const panelHosting = panelHostingFlag === true;

  useEffect(() => {
    if (!tenantId) return;
    // Back to unresolved on a tenant change: a super-user switching from a
    // flag-on tenant to a flag-off one would otherwise keep the previous
    // tenant's shell until the new answer arrived.
    setShellLayoutFlag(null);
    setPanelHostingFlag(null);
    let live = true;
    // Two questions, not two requests. Both helpers keep a per-tenant promise for
    // the life of the tab, and the AI one is already asked for by the chat panel
    // and the overview, so this joins a fetch that was happening anyway.
    fetchTreeInsightsEnabled(tenantId)
      .then((enabled) => {
        if (live) setShellLayoutFlag(enabled);
      })
      .catch(() => {
        if (live) setShellLayoutFlag(false);
      });
    fetchAiInsightsEnabled(tenantId)
      .then((enabled) => {
        if (live) setPanelHostingFlag(enabled);
      })
      .catch(() => {
        if (live) setPanelHostingFlag(false);
      });
    return () => {
      live = false;
    };
  }, [tenantId]);
  const {
    isOpen: isSalesCopilotModalOpen,
    closeModal: closeSalesCopilotModal,
  } = useSalesCopilotModalStore();
  const sidebarWidth = isCollapsed
    ? SIDEBAR_DIMENSIONS.MAIN_COLLAPSED_PX
    : SIDEBAR_DIMENSIONS.MAIN_EXPANDED_PX;

  // The shell measures rather than asking a media query, because the viewport is
  // a poor proxy for the page here: 1440 with the rail open gives the content
  // 1136px and 2560 gives it 2256px, and both are Bootstrap's `xxl`.
  const [viewport, setViewport] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth
  );
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight
  );

  // Either job needs the measurement: the layout paints a gutter from it and the
  // panel decides its fold from it.
  const measuring = shellLayout || panelHosting;

  useEffect(() => {
    if (!measuring) return undefined;
    const onResize = () => {
      setViewport(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    // Read once on enable too: the flag resolves after mount, so the initial
    // state was captured before anyone knew this shell would be used.
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measuring]);

  // Latched, not derived. The fit says when the panel stopped fitting; this
  // remembers it until the user reopens the panel, so room returning never
  // unfolds it on its own. Deriving it from width sprang the panel open as the
  // window grew and took 380px off the content in one step.
  //
  // Held in the side-panel store rather than in this component because the
  // occupant sets it too: the panel draws its own close control, and it renders
  // far below the shell with no other way to reach this state.
  const panelRailed = sidePanel.railed;
  const setPanelRailed = sidePanel.setRailed;
  // Held while a resize gesture is in flight, so the automatic fold cannot fire
  // mid-drag and pull the handle out from under the cursor.
  const [panelDragging, setPanelDragging] = useState(false);

  const fit = useMemo(
    () =>
      resolveLayoutFit({
        viewport,
        chatOpen: panelHosting && sidePanel.requested,
        // The column is the card plus its inset, and that whole width is what the
        // content does not get.
        chatWidth: sidePanel.width + LAYOUT.PANEL_INSET_PX,
        panelRailed,
      }),
    [viewport, panelHosting, sidePanel.requested, sidePanel.width, panelRailed]
  );

  // One direction only. Nothing here ever clears the latch: that is the user's
  // click, on the rail or the topbar toggle.
  // Keyed on the viewport as well as the signal. `chatAsRail` stays true for as
  // long as the window is narrow, so on the signal alone the effect could only
  // ever fire once: after the user clicked the rail open at a width with no room
  // for it, nothing re-folded it however much further they shrank the window,
  // and the protection was off for the rest of the session. Keyed this way the
  // user's choice stands at the width they made it, and any resize re-applies.
  //
  // `panelDragging` is READ here and deliberately not depended on, but NOT for the
  // reason this comment used to give.
  //
  // It once said the dependency was what made every drag fold the panel. That was
  // true when `chatAsRail` still answered to the width the panel had been dragged
  // to. It no longer does: `roomForPanel` is computed from CHAT_MIN_PX, the panel's
  // minimum, so `chatAsRail` is a fact about the VIEWPORT and the handle cannot
  // change it. All three return paths of `resolveLayoutFit` are independent of
  // `chatWidth`, which only feeds `mainWidth`. A drag therefore changes none of this
  // effect's dependencies and cannot re-run it at all.
  //
  // What the ref still guards is narrower and real: the viewport changing WHILE a
  // drag is in flight. That does re-run this, and folding then would pull the handle
  // out from under the cursor.
  //
  // Stated exactly because the old wording claimed credit for a fix that had moved.
  // A guard that misdescribes what it guards is how the same wrong fix gets written
  // twice, which is what happened here (see #2576, closed).
  const draggingRef = useRef(panelDragging);
  draggingRef.current = panelDragging;
  useEffect(() => {
    if (fit.chatAsRail && !draggingRef.current) setPanelRailed(true);
  }, [fit.chatAsRail, viewport, setPanelRailed]);

  // Routed through `setForcedCollapsed`, never `setCollapsed`, so the user's own
  // rail preference survives. Opening a panel once on a laptop must not leave the
  // rail collapsed on their 27" monitor forever after.
  // Written as an expression, not an early return: returning early left the
  // rail pinned shut by a force nothing was maintaining any more, which a
  // super-user hit by switching to a tenant without the flag.
  //
  // Two callers, one signal, and they are NOT OR-ed. `resolveLayoutFit` folds the
  // rail for two different reasons: to make room for an open panel, which belongs
  // to `panelHosting`, and because the window alone cannot hold `MAIN_MIN_PX`,
  // which is the new layout's rule and belongs to `shellLayout`. OR-ing them would
  // hand that second rule to an AI-only tenant, whose nav would start folding
  // itself under about 1200px with the chat shut: a shell behaviour on a tenant
  // that was deliberately not given the shell.
  const forceNavCollapsed = shellLayout
    ? fit.forceNavCollapsed
    : panelHosting && sidePanel.requested && fit.forceNavCollapsed;
  useEffect(() => {
    setForcedCollapsed(forceNavCollapsed);
  }, [forceNavCollapsed, setForcedCollapsed]);

  // Turning the flag off, or unmounting, must not leave the rail pinned shut by
  // a force nothing is maintaining any more.
  useEffect(() => () => setForcedCollapsed(false), [setForcedCollapsed]);

  // Published on the document root rather than on this element, because the
  // sidebar declares its own metrics on `:root` and is `position: fixed` outside
  // this subtree, so a value set here could never reach it. The top bar shrank to
  // 56px on a short window while the sidebar's logo block stayed at 64, leaving
  // its divider 8px below the top bar's border on any 1440x800 laptop.
  //
  // Every consumer still names today's value as its CSS fallback, so removing
  // these restores the current product exactly.
  useEffect(() => {
    const root = document.documentElement;
    const keys = [
      '--shell-gutter',
      '--shell-topbar-h',
      '--sidebar-logo-height',
    ];
    // `shellLayout` alone, not the panel. These are the measured metrics of the
    // new page layout; a tenant given only the assistant keeps today's chrome,
    // and every consumer names today's value as its CSS fallback.
    if (!shellLayout) {
      keys.forEach((k) => root.style.removeProperty(k));
      return undefined;
    }
    const topbar = topbarFor(viewportHeight);
    root.style.setProperty('--shell-gutter', `${fit.gutter}px`);
    root.style.setProperty('--shell-topbar-h', `${topbar}px`);
    root.style.setProperty('--sidebar-logo-height', `${topbar}px`);
    return () => keys.forEach((k) => root.style.removeProperty(k));
  }, [shellLayout, fit.gutter, viewportHeight]);

  // A column only exists when a page has announced an occupant for it. Reserving
  // one unconditionally would put an empty frame on every page that has nothing
  // to dock, and the occupant cannot portal into a host that is not there yet, so
  // the request has to lead and the column follow.
  // Folded, the column takes no width at all (`CHAT_RAIL_PX` is 0): what reopens
  // it is the Ask AI button in the model header, so nothing on the page is ever
  // covered. A column exists whenever a page has an occupant for
  // it; what folds it is the automatic fold when the window has no room, the
  // panel's own close control, or a second click on Ask AI.
  const panelShown = panelHosting && sidePanel.requested;

  // Where pages put their own top-bar controls (see `TopbarSlot`). State, not a
  // ref, so the pages portalling into it re-render once it exists.
  const [topbarSlot, setTopbarSlot] = useState<HTMLElement | null>(null);
  const panelColumnPx = !panelShown
    ? 0
    : panelRailed
    ? LAYOUT.CHAT_RAIL_PX
    : sidePanel.width + LAYOUT.PANEL_INSET_PX;

  // `main` is a grid for either job, so the class describing it follows both.
  // Nothing styles it today; it is here so the DOM says which mode is in effect.
  const gridded = shellLayout || panelHosting;

  const showOrgContextBanner = Boolean(
    isSuperKudu &&
      tenantId &&
      (!primaryTenantId || tenantId !== primaryTenantId)
  );
  const bannerHeightPx = showOrgContextBanner
    ? ORG_CONTEXT_BANNER_HEIGHT_PX
    : 0;
  const mainContainerHeight = showOrgContextBanner
    ? `calc(100vh - ${ORG_CONTEXT_BANNER_HEIGHT_PX}px)`
    : '100vh';

  // The Document360 knowledge-base assistant, on the tenants that do not have
  // the model chat.
  //
  // The two are the same offer in the same place: a launcher bubble in the
  // bottom-right corner that opens an assistant you type a question into. Run
  // together they overlap, and the user has to know which of two bubbles answers
  // which kind of question. So this one stands down wherever the model chat is
  // on, and tenants without it keep the knowledge base exactly as today.
  //
  // Keyed on the RESOLVED flag, not on `panelHosting`. The flag arrives after
  // mount, so `panelHosting` is false for the first beat of every page load, and
  // injecting on that first false to remove it a moment later does not work:
  // `widget.js` builds a launcher, an iframe and a welcome popover on
  // `document.body`, and removing the script tags leaves all of it on screen.
  // Nothing is injected until the answer is in, which costs the knowledge-base
  // tenants one beat and cannot leave a stranded widget behind.
  useEffect(() => {
    // `null` is "not resolved yet", and only an explicit false injects.
    if (panelHostingFlag !== false) return undefined;
    // Check if script already exists
    if (document.getElementById('mw')) return undefined;

    // Document360 knowledge base widget - inject as raw script
    const script = document.createElement('script');
    script.innerHTML = `
      (function (w,d,s,o,f,js,fjs) {
          w['JS-Widget']=o;w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) };
          js = d.createElement(s), fjs = d.getElementsByTagName(s)[0];
          js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs);
      }(window, document, 'script', 'mw', 'https://cdn.document360.io/static/js/widget.js'));
      mw('init', { apiKey: '1v9fUFV3l+4XZ+laMwCP8ktA+djQvUhnMztmA4GXuYWDtTBRNleQy1OWi9wovgURzRkbdPm6cPnpkZzNERhEMucOEQYLUA+QWw7uUVBPZY6pXqLKeIB7xWxy3rjst3emPrCNK0oxcO4QZ7ASYQzlcg==' });
    `;
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Remove the Document360 script
      const existingScript = document.getElementById('mw');
      if (existingScript) {
        existingScript.remove();
      }
      // Remove our injected script
      const scripts = document.head.querySelectorAll('script');
      scripts.forEach((s) => {
        if (s.innerHTML.includes('JS-Widget')) {
          s.remove();
        }
      });
      // And what the script BUILT, which the two lines above do not touch. The
      // widget mounts these on `document.body`, so removing only the tags left a
      // live launcher and iframe on the page: a super-user switching from a
      // knowledge-base tenant to a chat tenant kept both bubbles, which is the
      // exact overlap this gate exists to prevent.
      D360_NODE_IDS.forEach((id) => document.getElementById(id)?.remove());
    };
  }, [panelHostingFlag]);

  return (
    <ShellFlags
      shellLayout={shellLayout}
      panelHosting={panelHosting}
      topbarSlot={topbarSlot}
    >
      {showOrgContextBanner && tenantId && (
        <OrgContextBanner tenantId={tenantId} />
      )}

      <div
        style={{
          display: 'flex',
          height: mainContainerHeight,
          marginTop: showOrgContextBanner ? `${bannerHeightPx}px` : undefined,
          overflow: 'hidden',
        }}
      >
        {/* Sidebar */}
        <AppSidebar
          config={navConfig}
          logo={logo}
          collapsedLogo={collapsedLogo}
          logoHref={logoHref}
          className={showOrgContextBanner ? 'sidebar--org-context' : undefined}
        />

        {/* Page Container */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%',
            marginLeft: `${sidebarWidth}px`,
            // Off while the rail snaps, or the page would still slide.
            transition: suppressTransition
              ? 'none'
              : `margin-left ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            overflow: 'hidden',
            minWidth: 0, // Allows flex children to shrink below content size
          }}
        >
          {/* Topbar */}
          <header
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: `var(--shell-topbar-h, ${SIDEBAR_DIMENSIONS.TOP_BAR_HEIGHT})`,
              minHeight: `var(--shell-topbar-h, ${SIDEBAR_DIMENSIONS.TOP_BAR_HEIGHT})`,
              flexShrink: 0,
              padding: '0 16px',
              backgroundColor: '#ffffff',
              borderBottom: '1px solid var(--brand-prussian)',
              position: 'relative',
              zIndex: Z_INDEX.TOPBAR,
            }}
          >
            <div>
              {showAppSwitcher ? (
                <AppSwitcher
                  rbac={rbac ?? null}
                  tenantId={tenantId}
                  isSuperKudu={isSuperKudu}
                />
              ) : (
                <h6
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'var(--brand-prussian)',
                    fontFamily: 'var(--hg-font-display)',
                    margin: 0,
                    lineHeight: 1.2,
                  }}
                >
                  Data Studio
                </h6>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* CopilotIcon hidden in favor of Eddy AI widget */}
              <div
                ref={setTopbarSlot}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              />
              <AppTopbarUserMenu user={user} tenantId={tenantId} />
            </div>
          </header>

          {/* Main Content */}
          {/* One `main`, one wrapper, whatever the flag says. The shape of this
              tree must not change when the flag resolves: it was a ternary of two
              different `main` elements, which re-parented `children` a level
              deeper on the responsive branch, so React unmounted and remounted
              the whole page. The flag always resolves after first paint, so every
              page mounted TWICE for a flag-on tenant: Overview fetched its data
              twice, the trees refetched, and the chat minted a second thread.

              The panel is a real column rather than an overlay. The whole point
              of a docked assistant is reading the model while asking about it,
              which a drawer over a scrim defeats. `minmax(0, 1fr)` and not `1fr`:
              a bare `1fr` has an `auto` minimum, so one wide table would push the
              column past the shell instead of scrolling inside it. */}
          {/* Grid whenever either job is on. The layout wants it for the capped,
              container-queried content column; the panel wants it for a second
              column to put the card in. A tenant with only the panel gets a grid
              of one content column plus the panel, and the content column is the
              same `shell-content--off` flex child it is on master: it stretches
              to its grid column, still shrinks past its content, and nothing
              about the page inside it changes. */}
          <main
            className={gridded ? 'shell-main-grid' : undefined}
            style={
              gridded
                ? {
                    display: 'grid',
                    gridTemplateColumns: panelShown
                      ? `minmax(0, 1fr) ${panelColumnPx}px`
                      : 'minmax(0, 1fr)',
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0,
                  }
                : {
                    display: 'flex',
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0, // Allows flex child to shrink
                  }
            }
          >
            {/* The container context, when the flag is on. Pages inside query
                THIS, so they reflow to the width the panel and the rail actually
                left them rather than to a viewport that never changed. Off, it
                is a bare flex child that measures nothing and changes nothing. */}
            <div
              className={shellLayout ? 'shell-content' : 'shell-content--off'}
            >
              {children}
            </div>
            {panelShown && (
              <ShellPanel
                asRail={panelRailed}
                onDragChange={setPanelDragging}
                label="Ask about this model"
              />
            )}
          </main>
        </div>

        {/* Sales Copilot Modal - rendered at root level for proper portal behavior */}
        <SalesCopilotModal
          open={isSalesCopilotModalOpen}
          onOpenChange={closeSalesCopilotModal}
        />
      </div>
    </ShellFlags>
  );
}
